package com.arenaproplay.admin.ui.components.admin

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.domain.admin.KpiDelta
import com.arenaproplay.admin.ui.components.charts.svg.SparklineSvg
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.AppEffects
import com.arenaproplay.admin.ui.theme.eyebrow
import com.arenaproplay.admin.ui.theme.numLarge
import com.arenaproplay.admin.ui.theme.numSmall
import com.arenaproplay.admin.ui.theme.bodySMedium
import com.arenaproplay.admin.ui.theme.toHexString
import com.arenaproplay.admin.ui.utils.sBorderRadius
import com.arenaproplay.admin.ui.utils.sPadding
import com.varabyte.kobweb.compose.ui.graphics.Color
import kotlin.math.abs
import kotlin.math.roundToLong
import org.jetbrains.compose.web.css.*

/**
 * Court Light KPI card — eyebrow label, tabular numeral, delta pill,
 * and optional sparkline. This is the canonical v2 signature from the B2 brief.
 */
@Composable
fun KpiCard(
    label: String,
    value: String,
    delta: KpiDelta? = null,
    spark: List<Double>? = null,
    accentColor: Color = AppColors.Primary,
    modifier: Modifier = Modifier
) {
    val accentHex = "#${accentColor.toHexString()}"
    Box(
        modifier
            .fillMaxWidth()
            .fillMaxHeight()
            .sBorderRadius(16)
            .styleModifier {
                property("background", "#0B1220")
                property("border", "1px solid #1C2634")
                property("box-shadow", AppEffects.CardElevation)
                property("position", "relative")
                property("overflow", "hidden")
                property("min-height", "156px")
            }
    ) {
        Column(Modifier.fillMaxWidth().fillMaxHeight().sPadding(20)) {
            // Eyebrow label — 10px uppercase, muted
            SpanText(
                label,
                Modifier.eyebrow().color(AppColors.Faint)
            )

            Box(Modifier.height(10.px))

            // Tabular numeric value
            SpanText(
                value,
                Modifier.numLarge().color(AppColors.Ink)
            )

            if (spark != null && spark.size > 1) {
                Box(Modifier.height(10.px))
                SparklineSvg(
                    spark,
                    colorHex = accentHex,
                    width = 160,
                    height = 28
                )
            }

            Box(Modifier.weight(1f).styleModifier { property("min-height", "8px") })

            if (delta != null) {
                KpiDeltaPill(delta)
            }
        }
    }
}

@Composable
private fun KpiDeltaPill(delta: KpiDelta) {
    val pct = delta.percentDelta
    val isUp = (pct ?: 0.0) >= 0.0
    val fgColor = if (isUp) AppColors.Success else AppColors.Error
    val bgHex = if (isUp) "#072A21" else "#2A1714"
    val arrow = if (isUp) "↑" else "↓"
    Row(verticalAlignment = Alignment.CenterVertically) {
        Box(
            Modifier
                .sPadding(4, 10).sBorderRadius(999)
                .styleModifier { property("background", bgHex) }
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                SpanText(
                    arrow,
                    Modifier.fontSize(11.px).color(fgColor)
                        .styleModifier { property("font-weight", "700") }
                )
                Box(Modifier.width(4.px))
                SpanText(
                    "${pct?.let { abs(it).format(1) } ?: "—"}%",
                    Modifier.numSmall().color(fgColor)
                )
            }
        }
        Box(Modifier.width(10.px))
        SpanText(
            "vs prev period",
            Modifier.bodySMedium().color(AppColors.Faint)
        )
    }
}

// ── Utility functions ──────────────────────────────────────────────────────

fun formatNumber(n: Double): String {
    if (n.isNaN() || n.isInfinite()) return "—"
    val abs = abs(n)
    return when {
        abs >= 1_000_000 -> "${(n / 1_000_000).format(1)}M"
        abs >= 10_000 -> "${(n / 1_000).format(1)}K"
        abs >= 1_000 -> {
            val long = n.roundToLong()
            val s = abs(long).toString()
            val sep = s.reversed().chunked(3).joinToString(",").reversed()
            if (long < 0) "-$sep" else sep
        }
        else -> n.roundToLong().toString()
    }
}

fun formatNumber(n: Long): String = formatNumber(n.toDouble())

fun formatMoney(n: Double, currency: String = "UAH"): String {
    if (n.isNaN() || n.isInfinite()) return "— $currency"
    return "${formatNumber(n)} $currency"
}

fun Double.format(decimals: Int): String {
    val factor = (1..decimals).fold(1.0) { acc, _ -> acc * 10 }
    val rounded = kotlin.math.round(this * factor) / factor
    val s = rounded.toString()
    val parts = s.split(".")
    return if (parts.size == 2) {
        val frac = parts[1].take(decimals).padEnd(decimals, '0')
        "${parts[0]}.$frac"
    } else if (decimals == 0) s else "$s.${"0".repeat(decimals)}"
}

